import * as React from 'react';
import { Divider, Select, SelectProps } from '@duxton-web/ui';
import Fuse from 'fuse.js';
import { debounce, get } from 'lodash';
import styled from 'styled-components';

import { ControlledInputPropsBase } from '../../InputControlProps';

const { Option } = Select;

interface GenericFuseSelectProps<T extends object>
  extends Omit<
      ControlledInputPropsBase<string | string[] | number | number[]>,
      'onChange'
    >,
    Omit<React.HTMLAttributes<HTMLElement>, 'onChange' | 'onSelect'> {
  items: T[];
  mode?: 'multiple' | 'tags' | null;
  searchKeys?: string[];
  recordsPerPage?: number;
  renderOption?: (item: T) => React.ReactNode;
  onSearch?: (query: string) => void;
  customFilter?: (query: string, items: T[]) => T[];
  keyFields: {
    valueKey: keyof T;
    displayKey: keyof T;
  };
  onChange: (value: string | string[] | number | number[] | null) => void;
  getOptionLabel?: (item: T) => string;
  status?: SelectProps['status'];
  style?: React.CSSProperties;
}

const DEFAULT_RECORD_PER_PAGE = 20;

const GenericFuseSelect = <T extends object>({
  items,
  mode = null,
  value,
  onChange,
  disabled,
  searchKeys,
  recordsPerPage = DEFAULT_RECORD_PER_PAGE,
  renderOption,
  onSearch,
  customFilter,
  keyFields,
  getOptionLabel,
  status,
  style,
  ...restProps
}: GenericFuseSelectProps<T>) => {
  const [showItems, setShowItems] = React.useState<T[]>([]);
  const [searchItems, setSearchItems] = React.useState<T[]>([]);
  const [page, setPage] = React.useState(1);

  const selectRef = React.useRef<any>();
  const itemsMap = React.useRef<Map<string, T>>(new Map());
  const fuse = React.useRef<Fuse<T, Fuse.FuseOptions<T>>>(
    new Fuse(items, {
      keys: searchKeys || [keyFields.valueKey, keyFields.displayKey],
    }),
  );

  // Helper function to get paginated records
  const selectRecordsFromPage = (
    fromPage: number,
    toPage: number,
    records: T[],
    perPage: number,
  ) => {
    const start = fromPage * perPage;
    const end = toPage * perPage;
    return records.slice(start, end);
  };

  React.useEffect(() => {
    items.forEach((item) => {
      const itemValue = get(item, keyFields.valueKey);
      itemsMap.current.set(String(itemValue), item);
    });
    setShowItems(selectRecordsFromPage(0, 1, items, recordsPerPage));
    setSearchItems(items);
  }, [items, recordsPerPage, keyFields]);

  const reset = () => {
    setShowItems(selectRecordsFromPage(0, 1, items, recordsPerPage));
    setSearchItems(items);
    setPage(1);
  };

  const handleOnSearch = debounce((query: string) => {
    if (query === '') {
      reset();
      return;
    }

    if (onSearch) {
      onSearch(query);
      return;
    }

    let filteredItems: T[] = customFilter
      ? customFilter(query, items)
      : (fuse.current.search(query) as T[]);

    setSearchItems(filteredItems);
    setShowItems(selectRecordsFromPage(0, 1, filteredItems, recordsPerPage));
    setPage(1);
  }, 50);

  const loadNext = () => {
    const newItems = selectRecordsFromPage(
      page,
      page + 1,
      searchItems,
      recordsPerPage,
    );
    setPage((p) => p + 1);
    setShowItems(newItems);
  };

  const loadPrev = () => {
    const oldItems = selectRecordsFromPage(
      page - 2,
      page - 1,
      searchItems,
      recordsPerPage,
    );
    setPage((p) => p - 1);
    setShowItems(oldItems);
  };

  const renderDropdown = (menu: React.ReactNode) => {
    const listSize = showItems.length;
    const fromItemOrder = (page - 1) * recordsPerPage + 1;
    const toItemOrder = (page - 1) * recordsPerPage + listSize;

    return (
      <div>
        {menu}
        {listSize > 0 && (
          <React.Fragment>
            <Divider style={{ margin: '8px 0' }} />
            <ItemsOrderMessage>
              {`Showing items ${fromItemOrder} to ${toItemOrder} of ${searchItems.length}`}
            </ItemsOrderMessage>
            <div style={{ float: 'right', marginRight: '10px' }}>
              {page >= 2 && (
                <a onMouseDown={(e) => e.preventDefault()} onClick={loadPrev}>
                  {' '}
                  prev{' '}
                </a>
              )}
              {page <= Math.floor(searchItems.length / recordsPerPage) && (
                <a onMouseDown={(e) => e.preventDefault()} onClick={loadNext}>
                  {' '}
                  next{' '}
                </a>
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  };

  const defaultRenderOption = (item: T) => {
    const displayValue =
      getOptionLabel?.(item) || String(get(item, keyFields.displayKey));
    return (
      <Option
        key={String(get(item, keyFields.valueKey))}
        value={String(get(item, keyFields.valueKey))}
      >
        {displayValue}
      </Option>
    );
  };

  const options = showItems.map(renderOption || defaultRenderOption);

  const handleOnChange = (newValue: string | string[] | null) => {
    if (!newValue) {
      onChange(null);
      return;
    }

    if (Array.isArray(newValue)) {
      if (!newValue.length) {
        onChange([]);
        return;
      }

      const isNumeric = !isNaN(Number(newValue[0]));
      const convertedValues = isNumeric ? newValue.map(Number) : newValue;
      onChange(convertedValues.length > 0 ? convertedValues : null);
    } else {
      // If value is numeric, convert to number
      const isNumeric = !isNaN(Number(newValue));
      onChange(isNumeric ? Number(newValue) : newValue);
    }
  };

  // Convert input value to string for internal use
  const stringValue =
    value !== null && value !== undefined
      ? Array.isArray(value)
        ? value.map(String)
        : String(value)
      : undefined;

  return (
    <Select
      {...restProps}
      mode={mode}
      placeholder="Select value"
      onSearch={handleOnSearch}
      onChange={handleOnChange}
      showSearch
      filterOption={false}
      value={stringValue}
      dropdownRender={renderDropdown}
      disabled={disabled}
      ref={selectRef}
      showAction={['click', 'focus']}
      onBlur={reset}
      style={{ width: 'fit-content', minWidth: 200, ...style }}
      popupMatchSelectWidth={false}
      labelInValue={false}
      status={status}
    >
      {options}
    </Select>
  );
};

export default GenericFuseSelect;

const ItemsOrderMessage = styled.div`
  color: #a8a8a8;
  float: left;
  font-weight: bold;
  margin-bottom: 5px;
  margin-left: 12px;
  font-size: 12px;
`;
